<script setup lang="ts">
// The roster that runs down the right side of the Home screen: one hexagon per
// technician of the district, packed as two honeycombs — the active ones, then
// the rest — so the split is stated by the board rather than inferred from the
// payload's order. Everyone appears; a technician who is not active is the
// same cell with a muted ring, because this is the roster, not the map.
//
// The cell face is the technician's photo, or their initials on a muted fill
// when they have none. The ring is the technician's state — success green for
// active, grey for not. The badge is today's assignment load, completed over
// total, and is absent when there is none. The whole card is the link to
// Operations; the roster is the workbench's own subject, so clicking a cell
// opens the same place the Operations card did.
//
// The honeycomb tessellates only when consecutive rows are offset by half a
// cell, so the cells are chunked into rows here — the column count and the cell
// size both follow the card's measured width — rather than left to
// `flex-wrap`, which cannot shift alternate rows.
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { HomeTechnician } from "@/type/api";

const props = defineProps<{
	technicians: HomeTechnician[];
}>();

/** One rendered cell: the technician and the badge geometry derived from today. */
interface RosterCell {
	technician: HomeTechnician;
	/** Today's assignments, completed plus open. */
	total: number;
}

const cells = computed<RosterCell[]>(() =>
	props.technicians.map((technician) => {
		const { completed, open } = technician.assignments_today;
		return { technician, total: completed + open };
	}),
);

/** The tooltip on a cell — and on its photo, so hovering the face answers with
 *  the full name too rather than with the image's alt text. */
function cellTitle(cell: RosterCell): string {
	const name = cell.technician.user.display_name;
	return cell.technician.active_today
		? `${name} — active`
		: `${name} — not active`;
}

// A pointy-top hexagon of width w is w * 2/√3 tall; the rows then stride by
// three quarters of that height, which is what makes them tessellate. A cell is
// drawn as large as the column allows, up to this: past it the cells read as
// portraits rather than as the swarm of a district, and too few of them to fill
// the width would leave the widest row short of the card's edge.
const HEX_MAX = 96;

/** The width of one hexagon, in px: stretched to fill the measured column. */
const hexAcross = ref(HEX_MAX);
/** How many hexagons a row holds at that width. */
const columns = ref(1);
const hexHeight = computed(() =>
	Math.round((hexAcross.value * 2) / Math.sqrt(3)),
);

// The board both combs sit in. Its width is the card's, and it changes as the
// page reflows (the aside is a fixed-width column on a wide screen but the full
// width of the page until the row layout kicks in), so the cell size is
// measured rather than assumed.
const board = ref<HTMLElement | null>(null);

function measure() {
	const element = board.value;
	if (!element) return;
	const width = element.clientWidth;
	if (width <= 0) return;
	// An offset row is half a cell wider than the rest, so `c` columns need
	// `(c + 0.5) * size` of width. Take the fewest columns whose cell still fits
	// the cap — fewer columns means larger cells, and larger cells are what fill
	// a tall column — then stretch them to the measured width, so the widest row
	// ends at the card's edge instead of short of it.
	const fit = Math.max(1, Math.ceil(width / HEX_MAX - 0.5));
	columns.value = fit;
	hexAcross.value = width / (fit + 0.5);
}

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
	measure();
	const element = board.value;
	if (!element) return;
	resizeObserver = new ResizeObserver(measure);
	resizeObserver.observe(element);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

/** One comb's cells, chunked into offset rows in the order the payload sorted
 *  them. */
function combRows(group: RosterCell[]): RosterCell[][] {
	const rows: RosterCell[][] = [];
	for (let i = 0; i < group.length; i += columns.value) {
		rows.push(group.slice(i, i + columns.value));
	}
	return rows;
}

/** One labelled comb: its cells, and their packing. */
interface RosterGroup {
	key: string;
	label: string;
	/** The pill beside the label, coloured like the cells' rings. */
	pill: string;
	cells: RosterCell[];
	rows: RosterCell[][];
}

// The split is a partition of the payload, not a re-sort: the payload already
// puts the active technicians first, and each group keeps that order.
const groups = computed<RosterGroup[]>(() => {
	const active = cells.value.filter((cell) => cell.technician.active_today);
	const notActive = cells.value.filter((cell) => !cell.technician.active_today);
	return [
		{
			key: "active",
			label: "Active",
			pill: "text-bg-success",
			cells: active,
			rows: combRows(active),
		},
		{
			key: "not-active",
			label: "Not active",
			pill: "text-bg-light border",
			cells: notActive,
			rows: combRows(notActive),
		},
	];
});
</script>

<template>
	<RouterLink
		class="card h-100 home-roster text-decoration-none"
		to="/_/operations"
	>
		<div
			class="card-header d-flex align-items-center justify-content-between gap-2 py-2"
		>
			<span class="fw-semibold text-body">Technicians</span>
			<i class="bi bi-arrow-right text-primary" aria-hidden="true"></i>
		</div>
		<div class="card-body d-flex flex-column overflow-hidden py-2">
			<div
				ref="board"
				class="roster-board"
				:style="{
					'--hex-w': `${hexAcross}px`,
					'--hex-h': `${hexHeight}px`,
				}"
			>
				<div class="roster-scroll">
					<section
						v-for="group in groups"
						:key="group.key"
						class="roster-group"
					>
						<div class="roster-group-heading d-flex align-items-center gap-2">
							<span class="fw-semibold small text-body">{{ group.label }}</span>
							<span class="badge rounded-pill" :class="group.pill">
								{{ group.cells.length }}
							</span>
						</div>
						<div class="roster-comb">
							<div
								v-for="(row, rowIndex) in group.rows"
								:key="rowIndex"
								class="comb-row"
								:class="{ 'comb-row--offset': rowIndex % 2 === 1 }"
							>
								<div
									v-for="cell in row"
									:key="cell.technician.user.id"
									class="comb-cell"
									:title="cellTitle(cell)"
								>
									<!-- The ring is a slightly larger hexagon behind the
									     face: a clip-path hexagon cannot carry a normal
									     border. -->
									<div
										class="comb-ring"
										:class="
											cell.technician.active_today
												? 'comb-ring--active'
												: 'comb-ring--idle'
										"
									></div>
									<div class="comb-face">
										<img
											v-if="cell.technician.user.avatar"
											:src="cell.technician.user.avatar"
											:alt="cell.technician.user.display_name"
											:title="cellTitle(cell)"
											class="comb-photo"
										/>
										<span v-else class="comb-initials">
											{{ cell.technician.user.initials }}
										</span>
									</div>
									<span v-if="cell.total > 0" class="comb-badge">
										{{ cell.technician.assignments_today.completed }}/{{
											cell.total
										}}
									</span>
								</div>
							</div>
						</div>
					</section>
				</div>
			</div>
		</div>
		<div class="card-footer small text-muted py-2">
			<div class="d-flex flex-wrap align-items-center gap-2">
				<span class="legend-item">
					<span class="legend-hex legend-hex--active"></span>active
				</span>
				<span class="legend-item">
					<span class="legend-hex legend-hex--idle"></span>not active
				</span>
				<span class="legend-item">
					<span class="legend-badge">3/6</span>assignments done / today
				</span>
			</div>
		</div>
	</RouterLink>
</template>

<style scoped>
.home-roster {
	color: inherit;
	transition:
		box-shadow 0.15s ease,
		transform 0.15s ease;
}

.home-roster:hover {
	box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
	transform: translateY(-2px);
}

/* A pointy-top hexagon: the vertex is the midpoint of the top edge, so the
 * lattice needs alternate rows shifted by half a cell and pulled up by a
 * quarter of the cell height. */
.comb-ring,
.comb-face,
.legend-hex,
.legend-hex > span {
	clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
}

/* The board takes the card's leftover height, and the two combs inside it
 * scroll together, so a district bigger than the column still fits inside the
 * card. The element the width is measured from is the board and not the
 * scroller, so a scrollbar appearing cannot shrink the cells that caused it. */
.roster-board {
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	min-height: 0;
}

.roster-scroll {
	flex: 1 1 auto;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
}

/* The heading names the group and counts it; its spacing off the comb below is
 * a fraction of a cell, so the two scale together. */
.roster-group-heading {
	margin-bottom: calc(var(--hex-w) * 0.08);
}

.roster-group + .roster-group {
	margin-top: calc(var(--hex-w) * 0.2);
}

.comb-row {
	display: flex;
	height: var(--hex-h);
	margin-top: calc(var(--hex-h) * -0.25);
}

.comb-row:first-child {
	margin-top: 0;
}

.comb-row--offset {
	margin-left: calc(var(--hex-w) / 2);
}

.comb-cell {
	position: relative;
	flex: 0 0 auto;
	width: var(--hex-w);
	height: var(--hex-h);
}

.comb-ring {
	position: absolute;
	inset: 0;
}

.comb-ring--active {
	background: #198754;
}

.comb-ring--idle {
	background: #adb5bd;
}

/* The face sits inside the ring, leaving a hexagonal outline of the ring's
 * colour visible all the way round — a fraction of the cell, so the ring keeps
 * its weight as the cells grow. */
.comb-face {
	position: absolute;
	inset: calc(var(--hex-w) * 0.052);
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	background: #dee2e6;
}

.comb-photo {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

/* The fallback for a technician with no photo: their initials, centred on the
 * muted face, at a weight that reads as deliberate rather than missing. Sized
 * off the cell so it stays in proportion at any cell size. */
.comb-initials {
	color: #495057;
	font-size: calc(var(--hex-w) * 0.26);
	font-weight: 600;
	letter-spacing: 0.02em;
}

/* The badge sits in the cell's lower-left, clear of the face rather than over
 * it. The height is what makes that possible: the pointy-top hexagon only
 * widens to its full width at three-quarters height, so its left boundary at
 * this point is about 6% of the cell, and the badge starts just inside it. Its
 * own metrics are fractions of the cell, so it keeps its proportions. */
.comb-badge {
	position: absolute;
	left: 9%;
	bottom: 22%;
	padding: 0 calc(var(--hex-w) * 0.086);
	border-radius: 999px;
	background: rgba(33, 37, 41, 0.72);
	color: #fff;
	font-size: calc(var(--hex-w) * 0.155);
	line-height: calc(var(--hex-w) * 0.24);
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

.legend-item {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

/* The ring swatch mirrors a cell: a coloured hexagon with a pale hexagon
 * punched out of the middle. */
.legend-hex {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 14px;
	height: 16px;
}

.legend-hex--active {
	background: #198754;
}

.legend-hex--idle {
	background: #adb5bd;
}

.legend-hex > span {
	width: 8px;
	height: 10px;
	background: #fff;
}

.legend-badge {
	padding: 0 5px;
	border-radius: 999px;
	background: rgba(33, 37, 41, 0.72);
	color: #fff;
	font-size: 9px;
	line-height: 14px;
	font-variant-numeric: tabular-nums;
}
</style>
